import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useIdentity } from "../../auth/useIdentity";
import { getInvoicePrefs } from "../../api/invoicePrefs";
import {
  festiveTotals,
  listPromoCodes,
  listPromoUses,
  listPromotionUses,
  promoUseTotals,
  promotionTitle,
  type PromoUse,
} from "../../api/promotion";

/**
 * Promotions › Report (owner 2026-10-15: "build report for all promotion used
 * by which user ... and the total amount discount given"). Festive prices:
 * every plan sale inside a promotion's dates. Codes: every paid use of every
 * code. Who: the subscriber's name and mobile.
 */
export function PromotionReport() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  const ready = Boolean(identity.token && ns);

  const festive = useQuery({
    queryKey: ["promo-report-festive", ns],
    enabled: ready,
    retry: false,
    queryFn: () => listPromotionUses(identity, ns),
  });
  const codes = useQuery({
    queryKey: ["promo-report-codes", ns],
    enabled: ready,
    retry: false,
    queryFn: async () => {
      const all = await listPromoCodes(identity, ns);
      const out: (PromoUse & { code: string })[] = [];
      for (const c of all) {
        for (const u of await listPromoUses(identity, ns, c.code)) out.push({ ...u, code: c.code });
      }
      return out.filter((u) => u.status === "USED").sort((a, b) => b.at.localeCompare(a.at));
    },
  });
  const people = useQuery({
    queryKey: ["promo-people", ns],
    enabled: ready,
    retry: false,
    staleTime: 60_000,
    queryFn: ({ signal }) => getInvoicePrefs(identity, ns, signal),
  });
  const byAccount = new Map((people.data?.subscribers ?? []).map((s) => [s.accountid.toLowerCase(), s]));
  const who = (accountid: string, name = "", username = "") => {
    const s = byAccount.get(accountid.toLowerCase());
    const n = name || s?.name || s?.username || username;
    const m = s?.mobile || username;
    return n && m && n !== m ? `${n} · ${m}` : n || m || accountid;
  };
  const when = (at: string) => `${at.slice(0, 16).replace("T", " ")} UTC`;

  const festiveRows = festive.data ?? [];
  const codeRows = codes.data ?? [];
  const codeTotals = new Map<string, PromoUse[]>();
  for (const u of codeRows) codeTotals.set(u.code, [...(codeTotals.get(u.code) ?? []), u]);
  const now = new Date();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Promotion report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Who bought at a festive price or used a promotion code, and what it gave away. Festive prices count plan sales
        paid inside the promotion's dates; add-ons on the same receipt are not counted.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          onChange={(e) => setTarget(e.target.value.trim())} />
        <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
          Show
        </Button>
      </Stack>

      <Typography variant="h6" gutterBottom>
        Festive prices
      </Typography>
      {festive.isLoading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : festive.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Festive sales not loaded</AlertTitle>
          {(festive.error as Error).message}
        </Alert>
      ) : festiveRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Nobody has bought at a festive price yet.
        </Typography>
      ) : (
        <>
          <Table size="small" aria-label="Festive totals" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Promotion</TableCell>
                <TableCell align="right">Sales</TableCell>
                <TableCell align="right">Saved by subscribers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {festiveTotals(festiveRows).map((t) => (
                <TableRow key={t.promotion}>
                  <TableCell>{promotionTitle(t.promotion)}</TableCell>
                  <TableCell align="right">{t.sales}</TableCell>
                  <TableCell align="right">₹{t.saved}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Table size="small" aria-label="Festive sales" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Subscriber</TableCell>
                <TableCell>Promotion</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Normal</TableCell>
                <TableCell align="right">Festive</TableCell>
                <TableCell align="right">Saved</TableCell>
                <TableCell align="right">Receipt</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {festiveRows.map((u, i) => (
                <TableRow key={`${u.accountid}-${u.at}-${i}`}>
                  <TableCell>{who(u.accountid, u.name, u.username)}</TableCell>
                  <TableCell>{promotionTitle(u.promotion)}</TableCell>
                  <TableCell>{u.plan}</TableCell>
                  <TableCell align="right">₹{u.normalgross}</TableCell>
                  <TableCell align="right">₹{u.festivegross}</TableCell>
                  <TableCell align="right">₹{u.saved}</TableCell>
                  <TableCell align="right">₹{u.paid} {u.method}</TableCell>
                  <TableCell>{when(u.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Typography variant="h6" gutterBottom>
        Promotion codes
      </Typography>
      {codes.isLoading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : codes.error ? (
        <Alert severity="error">
          <AlertTitle>Code uses not loaded</AlertTitle>
          {(codes.error as Error).message}
        </Alert>
      ) : codeRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody has used a promotion code yet.
        </Typography>
      ) : (
        <>
          <Table size="small" aria-label="Code totals" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell align="right">Uses</TableCell>
                <TableCell align="right">Given off</TableCell>
                <TableCell align="right">Extra days given</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...codeTotals.entries()].map(([code, uses]) => {
                const t = promoUseTotals(uses, now);
                return (
                  <TableRow key={code}>
                    <TableCell>{code}</TableCell>
                    <TableCell align="right">{t.used}</TableCell>
                    <TableCell align="right">₹{t.rupeesOff}</TableCell>
                    <TableCell align="right">{t.bonusDays}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Table size="small" aria-label="Code uses">
            <TableHead>
              <TableRow>
                <TableCell>Subscriber</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Normal price</TableCell>
                <TableCell align="right">Given</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codeRows.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{who(u.accountid)}</TableCell>
                  <TableCell>{u.code}</TableCell>
                  <TableCell>{u.plan}</TableCell>
                  <TableCell align="right">₹{u.normalgross}</TableCell>
                  <TableCell align="right">{u.bonusdays > 0 ? `${u.bonusdays} extra days` : `₹${u.rupeesoff} off`}</TableCell>
                  <TableCell>{when(u.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
